import { api } from './api'
import type {
  Announcement,
  AnnouncementCategory,
  AnnouncementLinkEntityType,
  AnnouncementStatus,
  EstateDocument,
  PaginationMeta,
} from '@riverpark/types'

export interface ListAnnouncementsFilters {
  page?: number
  limit?: number
  category?: AnnouncementCategory
}

export async function listAnnouncements(
  filters: ListAnnouncementsFilters = {},
): Promise<{ data: Announcement[]; meta: PaginationMeta }> {
  const params = new URLSearchParams()
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.category) params.set('category', filters.category)
  const query = params.toString()
  return api.get<{ data: Announcement[]; meta: PaginationMeta }>(
    `/announcements${query ? `?${query}` : ''}`,
  )
}

export async function getAnnouncement(id: string): Promise<{ data: Announcement }> {
  return api.get<{ data: Announcement }>(`/announcements/${id}`)
}

export interface CreateAnnouncementInput {
  title: string
  body: string
  category: AnnouncementCategory
  pinned?: boolean
  public?: boolean
  status?: AnnouncementStatus
  sendSms?: boolean
  sendEmail?: boolean
}

export async function createAnnouncement(
  input: CreateAnnouncementInput,
): Promise<{ data: { id: string } }> {
  return api.post<{ data: { id: string } }>('/announcements', input)
}

export interface UpdateAnnouncementInput {
  title?: string
  body?: string
  category?: AnnouncementCategory
  pinned?: boolean
  public?: boolean
  status?: AnnouncementStatus
  /** Only take effect when this update is the draft -> published transition. */
  sendSms?: boolean
  sendEmail?: boolean
}

export async function updateAnnouncement(
  id: string,
  input: UpdateAnnouncementInput,
): Promise<{ data: { success: boolean } }> {
  return api.patch<{ data: { success: boolean } }>(`/announcements/${id}`, input)
}

export async function deleteAnnouncement(id: string): Promise<{ data: { success: boolean } }> {
  return api.delete<{ data: { success: boolean } }>(`/announcements/${id}`)
}

export async function uploadAnnouncementAttachment(
  announcementId: string,
  file: File,
): Promise<{ data: EstateDocument }> {
  const form = new FormData()
  form.set('file', file)
  return api.postForm<{ data: EstateDocument }>(
    `/announcements/${announcementId}/attachments`,
    form,
  )
}

/** Uploads an image to embed directly in the body via the rich-text editor — always publicly
 * viewable by URL (it's rendered as a plain <img src>), unlike a regular attachment. */
export async function uploadAnnouncementInlineImage(
  announcementId: string,
  file: File,
): Promise<{ data: EstateDocument }> {
  const form = new FormData()
  form.set('file', file)
  form.set('purpose', 'inline')
  return api.postForm<{ data: EstateDocument }>(
    `/announcements/${announcementId}/attachments`,
    form,
  )
}

export async function deleteAnnouncementAttachment(
  announcementId: string,
  documentId: string,
): Promise<{ data: { success: boolean } }> {
  return api.delete<{ data: { success: boolean } }>(
    `/announcements/${announcementId}/attachments/${documentId}`,
  )
}

export async function addAnnouncementLink(
  announcementId: string,
  entityType: AnnouncementLinkEntityType,
  entityId: string,
): Promise<{ data: { id: string } }> {
  return api.post<{ data: { id: string } }>(`/announcements/${announcementId}/links`, {
    entityType,
    entityId,
  })
}

export async function removeAnnouncementLink(
  announcementId: string,
  linkId: string,
): Promise<{ data: { success: boolean } }> {
  return api.delete<{ data: { success: boolean } }>(
    `/announcements/${announcementId}/links/${linkId}`,
  )
}
